import React, { useState } from 'react';

const NAME_PATTERN = /^[a-zA-Z0-9]+$/;

const getWebsiteNameError = (value) => {
  const name = value.trim();
  if (!name) return null;
  if (!NAME_PATTERN.test(name)) return 'Only letters and numbers allowed — no spaces or special characters.';
  if (name.length < 3) return 'Website name must be at least 3 characters.';
  return null;
};

function Step1({ formData, onInputChange, onSubmit, onStep1Change, showToast, disabled = false, trialCounter }) {
  const [checking, setChecking] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [nameError, setNameError] = useState(null);

  const CHECK_API_ENDPOINT = process.env.REACT_APP_FIELD_VALIDATION_API;

  const checkSubdomainAvailability = async (websiteName) => {
    if (!websiteName || websiteName.length < 3) {
      setAvailability(null);
      return;
    }

    setChecking(true);
    setAvailability(null);

    try {
      const response = await fetch(CHECK_API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'subdomain',
          value: websiteName.toLowerCase().trim()
        })
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setAvailability({
          available: data.available,
          message: data.message
        });
        
        if (data.available) {
          setTimeout(() => {
            setAvailability(null);
          }, 5000);
        }
      } else {
        setAvailability({
          available: false,
          message: 'Error checking availability. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error checking subdomain:', error);
      setAvailability({
        available: false,
        message: 'Error checking availability. Please try again.'
      });
    } finally {
      setChecking(false);
    }
  };

  const handleWebsiteNameChange = (rawValue) => {
    // Spaces are never valid in a subdomain — drop them as they are typed/pasted
    const value = rawValue.replace(/\s/g, '');

    onInputChange('websiteName', value);
    setAvailability(null);
    setNameError(null);

    if (window.subdomainCheckTimeout) {
      clearTimeout(window.subdomainCheckTimeout);
    }

    if (!value) {
      return;
    }

    window.subdomainCheckTimeout = setTimeout(() => {
      const error = getWebsiteNameError(value);

      if (error) {
        setNameError(error);
        return;
      }

      checkSubdomainAvailability(value);
    }, 800);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (disabled) {
      showToast('error', 'Trial limit reached. Cannot create more websites.');
      return;
    }

    // Validate all fields are filled
    if (!formData.websiteName.trim() || !formData.aboutUs || !formData.mainContent ||
        !formData.links || !formData.contactUs) {
      showToast('error', 'Please fill all required fields!');
      return;
    }

    // Validate website name format before anything else
    const websiteNameError = getWebsiteNameError(formData.websiteName);
    if (websiteNameError) {
      setNameError(websiteNameError);
      showToast('error', websiteNameError);
      return;
    }

    // Check if subdomain is available
    if (availability && !availability.available) {
      showToast('error', 'Website name is already taken. Please choose a different name.');
      return;
    }

    // Prepare Step 1 data
    const step1Data = {
      websiteName: formData.websiteName.toLowerCase().trim(),
      aboutUs: formData.aboutUs,
      mainContent: formData.mainContent,
      links: formData.links,
      contactUs: formData.contactUs
    };

    // Save to localStorage
    localStorage.setItem('websiteFormData', JSON.stringify(step1Data));
    console.log('Saved to localStorage:', step1Data);
    
    if (onStep1Change) {
      onStep1Change();
    }
    
    showToast('success', '✅ Step 1 data saved! Proceed to Step 2 to select your theme.');
    
    // Call parent onSubmit
    onSubmit();
  };

  return (
    <div className="step-card step-1">
      <div className="step-header-inline">
        <span className="step-number teal">1</span>
        <div>
          <h2 className="step-heading">Fill Your Content</h2>
          <p className="step-subtitle">Add your business details and content.</p>
        </div>
      </div>
      
      <div className="step-body">
        {trialCounter}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Choose Website Name</label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter your website name..."
              value={formData.websiteName}
              onChange={(e) => handleWebsiteNameChange(e.target.value)}
              required
              maxLength={20}
              title="Only letters and numbers allowed"
              disabled={disabled}
            />

            {nameError && (
              <div className="availability-status unavailable">
                <span className="icon-cross">✗</span>
                <span>{nameError}</span>
              </div>
            )}

            {checking && (
              <div className="availability-status checking">
                <span className="spinner-tiny"></span>
                <span>Checking availability...</span>
              </div>
            )}
            
            {availability && availability.available && (
              <div className="availability-status available">
                <span className="icon-check">✓</span>
                <span>{availability.message}</span>
              </div>
            )}
            
            {availability && !availability.available && (
              <div className="availability-status unavailable">
                <span className="icon-cross">✗</span>
                <span>{availability.message}</span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">About Us</label>
            <textarea
              className="form-control"
              placeholder="Tell us about your company..."
              value={formData.aboutUs}
              onChange={(e) => onInputChange('aboutUs', e.target.value)}
              rows="3"
              required
              disabled={disabled}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Main Content</label>
            <textarea
              className="form-control"
              placeholder="Describe your main services or products..."
              value={formData.mainContent}
              onChange={(e) => onInputChange('mainContent', e.target.value)}
              rows="3"
              required
              disabled={disabled}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Links</label>
            <input
              type="text"
              className="form-control"
              placeholder="Important links (comma separated)..."
              value={formData.links}
              onChange={(e) => onInputChange('links', e.target.value)}
              required
              disabled={disabled}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Contact Us</label>
            <input
              type="text"
              className="form-control"
              placeholder="Email, phone, address..."
              value={formData.contactUs}
              onChange={(e) => onInputChange('contactUs', e.target.value)}
              required
              disabled={disabled}
            />
          </div>

          <button 
            type="submit" 
            className="btn-custom btn-submit"
            disabled={disabled || (availability && !availability.available)}
          >
            <span className="icon">
              {disabled ? '🚫' : '↗'}
            </span>
            {disabled ? 'Trial Limit Reached' : 'Submit'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Step1;